import { useSearchParams } from 'react-router-dom';
import { useLoPublicSiteTranslation } from '@/hooks/useLoPublicSiteTranslation';
import type { LoPublicFormationCard } from '@/lib/lyrraone-public';
import {
  LoFormationCardGrid,
  LoFormationMarketplaceCard,
} from '@/components/lyrraone/formation-cards';
import { publicSiteLinkLang } from '@/lib/lo-public-site-locale';
import { creatorFormationHref } from '@/lib/lyrraone-site-links';
import { usePublicRegionStore } from '@/stores/public-region.store';
import { formatLyrraOnePrice } from '@/lib/store-formation';

interface Props {
  formations: LoPublicFormationCard[];
  siteSlug: string;
  customDomainSlug?: string | null;
  emptyMessage?: string;
  creatorName?: string | null;
}

export function LoPublicFormationGrid({
  formations,
  siteSlug,
  customDomainSlug,
  emptyMessage,
  creatorName,
}: Props) {
  const { t } = useLoPublicSiteTranslation();
  const region = usePublicRegionStore((s) => s.region);
  const [searchParams] = useSearchParams();
  const linkLang = publicSiteLinkLang(searchParams.get('lang'));
  const resolvedEmpty = emptyMessage ?? (t('formations.empty') as string);

  if (formations.length === 0) {
    return <p className="text-center text-gray-500">{resolvedEmpty}</p>;
  }

  return (
    <LoFormationCardGrid>
      {formations.map((f) => {
        const href = creatorFormationHref(f, siteSlug, customDomainSlug, linkLang);
        const resolvedPrice = f.price <= 0
          ? (t('formations.free') as string)
          : formatLyrraOnePrice(f.price, region);
        const category = f.creationMode === 'simple'
          ? (t('formationDetail.modeSimple') as string)
          : (t('formationDetail.modeAdvanced') as string);

        return (
          <LoFormationMarketplaceCard
            key={f.id}
            id={f.id}
            title={f.title}
            href={href}
            priceLabel={resolvedPrice}
            category={category}
            levelLabel={t('formations.levelAll') as string}
            authorName={creatorName?.trim() || null}
            coverImageUrl={f.coverImageUrl}
          />
        );
      })}
    </LoFormationCardGrid>
  );
}
